import * as m from 'mithril';
import './app-main.scss';
import AppContext from './context/AppContext';

import { RootFsmScheme, rootMobileAppFsmBuilder } from './states/root';
import { Fsm } from 'little-fsm';
import { buildTripSearchPresenter } from './presenters/TripSearchPresenter';
import { buildHomePresenter } from './presenters/HomePresenter';
import SearchFormModel from './models/SearchFormModel';
import { HomeFsmContext } from './states/home/HomeFsmContext';
import { buildSeatReservationPresenter } from './presenters/SeatSelectionPresenter';
import { buildTentativeBookingCreationPresenter } from './presenters/TentativeBookingCreationPresenter';
import { parseAppConfig } from './app.config';
import { buildReviewPresenter } from './presenters/ReviewPresenter';
import handleRouting from './routing-history-api';
import { buildPaymentSelectionPresenter } from './presenters/PaymentSelectionPresenter';
import { buildPaymentCompletedPresenter } from './presenters/PaymentCompletedPresenter';
import { ApiService } from './ApiService';
import { SourceLocationDTO } from './apiclient/ApiDtos';
import { ApiAccessType, ApiMode, IApiClient } from './apiclient/IApiClient';
import { ApiAccess, ApiClient } from './apiclient/ApiClient';
import { buildPickUpDropOffSelectionPresenter } from './presenters/PickUpDropOffSelectionPresenter';

let rootElem = document.getElementById('app-root');
document.addEventListener('DOMContentLoaded', async function () {
    initializeApp(rootElem!);
});


async function initializeApp(rootElem: HTMLElement) {
    let appConfig = parseAppConfig(rootElem);

    let superAppToken = "mock-superapp-token";
    if (rootElem.hasAttribute("data-super-app-token")) {
        const superAppTokenValue = rootElem.getAttribute("data-super-app-token");
        if (superAppTokenValue) {
            superAppToken = superAppTokenValue;
        }
    }

    let ajaxAccessToken: string = rootElem.getAttribute("data-ajax-access-token") || '';
    if(ajaxAccessToken == '' || ajaxAccessToken == null){
        return;
    }

    if (appConfig) {
        console.info("App configuration is parsed:", appConfig);

        let apiBaseUrls = appConfig.apiBaseUrls;
        console.log("Candidates for api base url:", apiBaseUrls);

        const workingApiBaseUrl = await selectApiBaseUrl(apiBaseUrls);
        if (workingApiBaseUrl) {
            console.log("Selecting base url:", workingApiBaseUrl);

            const apiAccessType: ApiAccessType = { mode: ApiMode.WebOrigin, headers: { 'X-XSRF-TOKEN': ajaxAccessToken } }
            let apiAccess: ApiAccess = new ApiAccess(apiAccessType);
            let apiClient: IApiClient = new ApiClient(workingApiBaseUrl, apiAccess);

            let apiService: ApiService = new ApiService(apiClient);
            let appContext = new AppContext(appConfig.appId, appConfig.merchantAppId, appConfig.initialLang, appConfig.externalMenuItems);
            let hasParentHome: boolean = rootElem!.getAttribute("data-has-parent-home")! == 'true';


            let masterDataResult = await apiService.getMasterData(appConfig.appId)
            if (masterDataResult.right) {
                console.log("Both token and data ready. Initializing app...");
                setupAppUI(
                    <Element>rootElem, appContext, apiService, masterDataResult.right.sources,
                    masterDataResult.right.shuttleSources, hasParentHome,
                    appConfig.smallCarSampleImageUrlPath
                );

            } else {
                console.error("Failed to fetch master data");
            }
        } else {
            console.error("None of api base url is working.");
        }
    } else {
        console.error("App configuration could not be parsed.");
    }
}


async function selectApiBaseUrl(apiBaseUrls: string[]): Promise<string> {
    const attempts = apiBaseUrls.map(apiBaseUrl =>
        checkApiBaseUrl(apiBaseUrl).then(ok => {
            if (!ok) throw new Error("Failed: " + apiBaseUrl);
            return ok;
        })
    );
    return Promise.any(attempts); // resolves with the first success
}


async function checkApiBaseUrl(apiBaseUrl: string) {
    try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 1000 * 30); // 30s timeout
        const res = await fetch(apiBaseUrl + "/health", { signal: controller.signal });
        clearTimeout(timeout);
        if (res.ok) {
            return apiBaseUrl; // this api base url works
        }
    } catch (e) {
        // failed (blocked or timeout)
    }
    return null;
}


function setupAppUI(rootElem: Element, appContext: AppContext, apiService: ApiService, sourceLocations: SourceLocationDTO[], shuttleSources: SourceLocationDTO[], hasParentHome: boolean, smallCarSampleImageUrlPath: string) {

    let fsm: Fsm<RootFsmScheme> = rootMobileAppFsmBuilder('pwa');

    handleRouting(fsm);
    const homePresenter = buildHomePresenter(appContext, apiService, sourceLocations, shuttleSources, fsm.subFsm('home'), false, false)
    const searchPresenter = buildTripSearchPresenter(appContext, apiService, sourceLocations, shuttleSources, smallCarSampleImageUrlPath, fsm.subFsm('search'))

    const seatSelectionPresenter = buildSeatReservationPresenter(appContext, apiService, fsm.subFsm('seat_reservation'))
    const wayPointOrderPresenter = buildPickUpDropOffSelectionPresenter(appContext, apiService, fsm.subFsm('pickup_dropoff_selection'))

    const tentativeBookingCreationPresenter = buildTentativeBookingCreationPresenter(appContext, apiService, fsm.subFsm('tentative_booking_creation'))
    const reviewPresenter = buildReviewPresenter(appContext, apiService, fsm.subFsm('review'));
    const paymentSelectionPresenter = buildPaymentSelectionPresenter(appContext, apiService, fsm.subFsm('payment_selection'));
    const paymentCompletedPresenter = buildPaymentCompletedPresenter(appContext, apiService, fsm.subFsm('payment_duplicate'));

    fsm.init("home", new HomeFsmContext(new SearchFormModel(null, null, null, 1, null), false));

    m.mount(rootElem, {
        view: function () {
            let currState = fsm.getCurrentState();

            if (currState == 'home') {
                return homePresenter.render();
            } else if (currState == 'search' || currState == 'choice_after_trip_selected') {
                return searchPresenter.render();
            } else if (currState == 'seat_reservation' || currState == 'choice_after_seat_reservation') {
                return seatSelectionPresenter.render();
            } else if (currState == 'pickup_dropoff_selection') {
                return wayPointOrderPresenter.render();
            } else if (currState == 'tentative_booking_creation' || currState == 'tentative_booking_creation_termination_decision') {
                return tentativeBookingCreationPresenter.render();
            } else if (currState == 'review') {
                return reviewPresenter.render();
            } else if (currState == 'payment_selection') {
                return paymentSelectionPresenter.render();
            } else if (currState == 'payment_duplicate') {
                return paymentCompletedPresenter.render();
            } else {
                return m("div", `No screen for the given state ${currState}`);
            }
        }
    });
}
